import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  CircularProgress,
  Alert as MuiAlert,
  Checkbox
} from '@/ui';
import { useNavigate } from 'react-router-dom';
import {
  SpeedIcon,
  CheckCircleIcon,
  SecurityIcon,
  WarningIcon,
  CloseIcon
} from '@/ui';
import BottomTabBar from '@/components/BottomTabBar';
import Header from '@/components/Header';
import { httpClient } from '@/shared/api/http';
import { uwOBEndpoints } from '@/shared/api/uwEndpoints';
import { getCustomerId } from '@/shared/lib/utils/getCustomerId';
import IncomeOnboardingOverlay from '@/components/onboarding/IncomeOnboardingOverlay';
import { OnboardingService } from '@/shared/api/services';
import { useAuth } from '@/shared/state/store';
import {
  INCOME_ONBOARDING_STEP_OFFSET,
  incomeOnboardingSchemas,
} from '@/pages/private/noNavButFooter/UpdateBudgetPageCom';

interface OpenBankingResponse {
  success: boolean;
  bankLink?: string;
  providerLogoUri?: string;
  providerDisplayName?: string;
  status?: string;
  successful?: boolean;
  inProgress?: boolean;
}

interface BenefitItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const BenefitItem: React.FC<BenefitItemProps> = ({ icon, title, description }) => (
  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
    <Box
      sx={{
        backgroundColor: '#f2f7f7',
        borderRadius: '50%',
        width: { xs: 32, md: 37 },
        height: { xs: 32, md: 37 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          fontSize: { xs: '16px', md: '16px' },
          color: '#1a202c',
          mb: 0.5,
          lineHeight: 'normal'
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: { xs: '14px', md: '14px' },
          color: '#4a5568',
          lineHeight: 'normal'
        }}
      >
        {description}
      </Typography>
    </Box>
  </Box>
);

const OpenBankingPage: React.FC = () => {
  const navigate = useNavigate();
  const { openBankingOnly, logout, user } = useAuth();
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [pendingConnect, setPendingConnect] = useState(false);
  const [shouldRenderOnboarding, setShouldRenderOnboarding] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);
  // LMX requires APITermsAgreedVersion: '1' on every Fresh Search.
  // Customer must explicitly agree before we initiate the bank handshake.
  const [tosAccepted, setTosAccepted] = useState(false);

  // Reusable T&Cs checkbox shown above each "connect my bank" button.
  const tosCheckbox = (
    <Box sx={{ mt: 1 }}>
      <Checkbox
        value={tosAccepted}
        onChange={(v: boolean) => setTosAccepted(v)}
        label={
          <Typography variant="body2" sx={{ fontSize: '14px', color: '#1a202c' }}>
            I agree to{' '}
            <a
              href="https://www.oohmoolah.co.uk/terms"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#003d3b', fontWeight: 600 }}
            >
              oohMoolah&apos;s Terms of Service
            </a>{' '}
            for Open Banking.
          </Typography>
        }
      />
    </Box>
  );

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    if (pendingConnect) {
      setPendingConnect(false);
      void handleConnectBank(true);
    } else {
      setShouldRenderOnboarding(false);
    }
  };

  const handleOnboardingCancel = () => {
    setPendingConnect(false);
    setShowOnboarding(false);
    setShouldRenderOnboarding(false);
  };

  const startOpenBanking = async (customerId: number) => {
    try {
      // New UW system: call our own /uw/ob/initiate endpoint (direct-to-LMX).
      // Previously called the iehub proxy endpoint; see
      // Backend-IeHub-Uw-Customer/DocsAndPlan/OpenBanking/decisions.md.
      const response = await httpClient.post<OpenBankingResponse>(
        uwOBEndpoints.initiate,
        { customerId }
      );

      if (response.data.success && response.data.bankLink) {
        // Redirect to bank authentication page.
        // Bank will redirect back to /ob-confirm-transaction?SearchID=xxx
        window.location.href = response.data.bankLink;
      } else {
        setError('Failed to get bank link. Please try again.');
        setLoading(false);
      }
    } catch (err: any) {
      // Handle MISSING_PERSONAL_DETAILS (422) from the backend PII validator.
      const code = err?.response?.data?.error?.code;
      const labels: string[] | undefined = err?.response?.data?.error?.missingFieldLabels;
      if (code === 'MISSING_PERSONAL_DETAILS' && labels?.length) {
        setError(`Please complete your personal details first: ${labels.join(', ')}.`);
      } else {
        setError(err?.response?.data?.error?.message || 'Failed to connect to Open Banking. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleConnectBank = async (skipOnboardingCheck = false) => {
    setLoading(true);
    setError(null);

    try {
      const customerId = await getCustomerId();

      if (!customerId) {
        setError('Unable to identify customer. Please try again or contact support.');
        setLoading(false);
        return;
      }

      // Creditor invited "Open Banking-only" users should not see the secondary
      // income onboarding overlay; they are locked to the Open Banking journey.
      const shouldSkipIncomeOnboarding = skipOnboardingCheck || openBankingOnly;

      if (!shouldSkipIncomeOnboarding) {
        const resp = await OnboardingService.getCustomerFieldStage(Number(customerId), {
          skipErrorCapture: true
        });
        const totalSteps = incomeOnboardingSchemas.length;
        const parsed = resp?.fieldStage ? parseInt(resp.fieldStage, 10) : NaN;
        const completed =
          Number.isFinite(parsed) && parsed >= INCOME_ONBOARDING_STEP_OFFSET + totalSteps;

        if (!completed) {
          setPendingConnect(true);
          setShouldRenderOnboarding(true);
          setShowOnboarding(true);
          setLoading(false);
          return;
        }
      }

      await startOpenBanking(Number(customerId));
    } catch (err: any) {
      console.error('Failed to start Open Banking flow', err);
      const errorMessage =
        err.response?.data?.message || 'Failed to connect to Open Banking. Please try again.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (openBankingOnly) {
      setConfirmLogoutOpen(true);
      return;
    }
    setConfirmCancelOpen(true);
  };

  const handleConfirmCancel = () => {
    setConfirmCancelOpen(false);
    navigate('/dashboard');
  };

  const handleCancelDialogClose = () => {
    setConfirmCancelOpen(false);
  };

  const handleLogoutDialogClose = () => {
    setConfirmLogoutOpen(false);
  };

  const handleConfirmLogout = async () => {
    setConfirmLogoutOpen(false);
    try {
      // Legacy parity: creditor Open Banking invitation journeys should mark the invite as rejected
      // when the user cancels from the Open Banking start page.
      try {
        if (openBankingOnly) {
          await OnboardingService.rejectOpenBankInvitationIfPresent(user, {
            skipErrorCapture: true,
          });
        }
      } catch (inviteError) {
        // Non-blocking: proceed with logout even if this fails.
        // eslint-disable-next-line no-console
        console.warn('Failed to update Open Banking invitation status (rejected)', inviteError);
      }

      await logout({ source: 'manual' });
    } finally {
      navigate('/auth', { replace: true });
    }
  };

  const handleDecline = () => {
    // TODO: Implement manual entry flow
    console.log('User chose manual entry');
    if (openBankingOnly) return;
    navigate(-1);
  };

  const handleConnectClick = () => {
    void handleConnectBank();
  };

  const benefits = [
    {
      icon: <SpeedIcon sx={{ fontSize: { xs: 14, md: 16 }, color: '#003d40' }} />,
      title: 'Faster completion',
      description: 'No need to manually type in your transactions.'
    },
    {
      icon: <CheckCircleIcon sx={{ fontSize: { xs: 14, md: 16 }, color: '#003d40' }} />,
      title: 'Increased accuracy',
      description: 'Reduce errors by importing data directly.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: { xs: 14, md: 16 }, color: '#003d40' }} />,
      title: 'Safe & secure',
      description: 'We use bank-level security and will never see your login details.'
    }
  ];

  return (
    <>
      {shouldRenderOnboarding && (
        <IncomeOnboardingOverlay
          open={showOnboarding}
          onComplete={handleOnboardingComplete}
          onCancel={handleOnboardingCancel}
        />
      )}
      {/* Mobile View */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          height: '100dvh',
          width: '100vw',
          backgroundColor: '#f9fafa',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Header />

        {/* Mobile Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3,
            pb: 10
          }}
        >
          {/* Error Alert */}
          {error && (
            <MuiAlert 
              severity="error" 
              onClose={() => setError(null)}
              sx={{ mb: 2 }}
            >
              {error}
            </MuiAlert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Title Section */}
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: '24px',
                  lineHeight: '32px',
                  color: '#1a202c',
                  mb: 2
                }}
              >
                Connect with open banking
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: '#4a5568'
                }}
              >
                Save time and fill out your budget form faster and more accurately. Securely connect your bank account to automatically import your spending and payments.
              </Typography>
            </Box>

            {/* Benefits Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {benefits.map((benefit, index) => (
                <BenefitItem
                  key={index}
                  icon={benefit.icon}
                  title={benefit.title}
                  description={benefit.description}
                />
              ))}
            </Box>

            {/* Warning Section */}
            {!openBankingOnly &&    <Box
              sx={{
                backgroundColor: '#fff5f5',
                borderLeft: '4px solid #e53e3e',
                borderTopRightRadius: '6px',
                borderBottomRightRadius: '6px',
                p: 2
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <WarningIcon sx={{ fontSize: 14, color: '#e53e3e', mt: 0.3 }} />
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#000',
                      mb: 0.5
                    }}
                  >
                    Please note:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '14px',
                      color: '#000',
                      lineHeight: 'normal'
                    }}
                  >
                    Choosing "yes" will replace any information you've already entered manually with data from your bank.
                  </Typography>
                </Box>
              </Box>
            </Box> }

            {/* T&Cs (LMX requires APITermsAgreedVersion: '1') */}
            {tosCheckbox}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleConnectClick}
                disabled={loading || !tosAccepted}
                sx={{
                  backgroundColor: '#d4e03b',
                  color: '#003d3b',
                  textTransform: 'none',
                  borderRadius: '999px',
                  py: 1.5,
                  fontSize: '16px',
                  fontWeight: 700,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#c5d630',
                    boxShadow: 'none'
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e'
                  }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: '#003d3b' }} />
                    Connecting...
                  </>
                ) : (
                  'Yes, connect my bank'
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  color: '#004b49',
                  borderColor: '#cbd5e0',
                  borderWidth: 2,
                  textTransform: 'none',
                  borderRadius: '999px',
                  py: 1.5,
                  fontSize: '16px',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#cbd5e0',
                    borderWidth: 2,
                    backgroundColor: 'rgba(0,0,0,0.02)'
                  }
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Bottom Tab Bar */}
        <BottomTabBar />
      </Box>

      {/* Desktop View */}
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          backgroundColor: '#f5f5f5',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Header />

        {/* Desktop Content - Centered Modal */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4
          }}
        >
          <Paper
            sx={{
              maxWidth: '896px',
              width: '100%',
              mx: 2,
              p: 6,
              borderRadius: '16px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Error Alert */}
            {error && (
              <MuiAlert 
                severity="error" 
                onClose={() => setError(null)}
                sx={{ mb: 3 }}
              >
                {error}
              </MuiAlert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Title */}
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 400,
                  fontSize: '36px',
                  lineHeight: '40px',
                  color: '#1a202c'
                }}
              >
                Connect with open banking
              </Typography>

              {/* Description */}
              <Typography
                variant="body1"
                sx={{
                  fontSize: '18px',
                  lineHeight: '28px',
                  color: '#4a5568'
                }}
              >
                Save time and fill out your budget form faster and more accurately. Securely connect your bank account to automatically import your spending and payments.
              </Typography>

              {/* Benefits Section */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {benefits.map((benefit, index) => (
                  <BenefitItem
                    key={index}
                    icon={benefit.icon}
                    title={benefit.title}
                    description={benefit.description}
                  />
                ))}
              </Box>

              {/* Warning Section */}
              {!openBankingOnly &&    <Box
                sx={{
                  backgroundColor: '#fff5f5',
                  borderLeft: '4px solid #e53e3e',
                  borderTopRightRadius: '6px',
                  borderBottomRightRadius: '6px',
                  p: 2.5
                }}
              >
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                  <WarningIcon sx={{ fontSize: 16, color: '#e53e3e', mt: 0.5 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#000',
                        mb: 0.5,
                        lineHeight: '24px'
                      }}
                    >
                      Please note:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '14px',
                        color: '#000',
                        lineHeight: '20px'
                      }}
                    >
                      Choosing "yes" will replace any information you've already entered manually with data from your bank.
                    </Typography>
                  </Box>
                </Box>
              </Box> }

              {/* T&Cs (LMX requires APITermsAgreedVersion: '1') */}
              {tosCheckbox}

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    color: '#004b49',
                    borderColor: '#cbd5e0',
                    borderWidth: 2,
                    textTransform: 'none',
                    borderRadius: '999px',
                    px: 3.5,
                    py: 1.75,
                    fontSize: '16px',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#cbd5e0',
                      borderWidth: 2,
                      backgroundColor: 'rgba(0,0,0,0.02)'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                onClick={handleConnectClick}
                  disabled={loading || !tosAccepted}
                  sx={{
                    backgroundColor: '#d4e03b',
                    color: '#003d3b',
                    textTransform: 'none',
                    borderRadius: '999px',
                    px: 5.5,
                    py: 1.75,
                    fontSize: '16px',
                    fontWeight: 600,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#c5d630',
                      boxShadow: 'none'
                    },
                    '&:disabled': {
                      backgroundColor: '#e0e0e0',
                      color: '#9e9e9e'
                    }
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: '#003d3b' }} />
                      Connecting...
                    </>
                  ) : (
                    'Yes, connect my bank'
                  )}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Mobile Modal Version (Alternative Design) */}
      <Dialog
        open={showMobileModal}
        onClose={() => setShowMobileModal(false)}
        fullScreen
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDialog-paper': {
            backgroundColor: 'white',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header with Close Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '18px',
                color: '#000'
              }}
            >
              Open Banking
            </Typography>
            <CloseIcon
              onClick={() => setShowMobileModal(false)}
              sx={{ fontSize: 14, cursor: 'pointer' }}
            />
          </Box>

          {/* Icon */}
          <Box
            sx={{
              width: 68,
              height: 68,
              borderRadius: '50%',
              backgroundColor: 'rgba(212, 233, 57, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3
            }}
          >
            <Box
              sx={{
                width: 47.6,
                height: 47.6,
                borderRadius: '50%',
                backgroundColor: '#d4e939',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <SecurityIcon sx={{ fontSize: 25.5, color: '#003d40' }} />
            </Box>
          </Box>

          {/* Title and Description */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: '30px',
              lineHeight: '36px',
              color: '#003d40',
              mb: 2.5
            }}
          >
            Speed up with Open Banking
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '16px',
              lineHeight: '24px',
              color: '#6b7280',
              mb: 3.5
            }}
          >
            Connect your bank account to automatically fill in your spending and payments. It's fast, secure, and saves you from typing everything yourself.
          </Typography>

          {/* Steps */}
          <Box
            sx={{
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              p: 2,
              mb: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827'
              }}
            >
              Step 1: Click "Continue with Open Banking"
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827'
              }}
            >
              Step 2: Connect your bank account securely
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827'
              }}
            >
              Step 3: Review and Submit your details
            </Typography>
          </Box>

          {/* Warning */}
          {!openBankingOnly &&    <Box
            sx={{
              backgroundColor: '#fef2f2',
              borderLeft: '4px solid #dc2626',
              borderRadius: '6px',
              p: 2,
              mb: 3
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <WarningIcon sx={{ fontSize: 16, color: '#dc2626', mt: 0.5 }} />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '14px',
                  color: '#000',
                  lineHeight: '20px'
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Please note:
                </Box>{' '}
                Connecting your bank will replace any information you've already typed in manually.
              </Typography>
            </Box>
          </Box> }

          {/* Question */}
          <Typography
            variant="h6"
            sx={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#111827',
              textAlign: 'center',
              mb: 3
            }}
          >
            How would you like to proceed?
          </Typography>

          {/* T&Cs (LMX requires APITermsAgreedVersion: '1') */}
          {tosCheckbox}

          {/* Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              pt: 3,
              borderTop: '1px solid #e5e7eb'
            }}
          >
            <Button
              variant="contained"
                onClick={handleConnectClick}
              disabled={loading || !tosAccepted}
              sx={{
                backgroundColor: '#d4e939',
                color: '#003d40',
                textTransform: 'none',
                borderRadius: '12px',
                py: 1.75,
                fontSize: '18px',
                fontWeight: 700,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#c5d630',
                  boxShadow: 'none'
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e'
                }
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: '#003d40' }} />
                  Connecting...
                </>
              ) : (
                'Yes, use Open Banking'
              )}
            </Button>
            {!openBankingOnly && (
              <Button
                variant="text"
                onClick={handleDecline}
                sx={{
                  color: '#6b7280',
                  textTransform: 'none',
                  borderRadius: '12px',
                  py: 1.75,
                  fontSize: '16px',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.02)'
                  }
                }}
              >
                No, I'll fill it out myself
              </Button>
            )}
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={confirmCancelOpen}
        onClose={handleCancelDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 1.5 }}
          >
            Cancel Open Banking?
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 3 }}
          >
            Are you sure you want to cancel the Open Banking journey and return to your dashboard?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleCancelDialogClose}
              sx={{ textTransform: 'none' }}
            >
              Stay on this page
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmCancel}
              sx={{ textTransform: 'none' }}
            >
              Yes, cancel
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={confirmLogoutOpen}
        onClose={handleLogoutDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
            Logout?
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Are you sure you want to logout? You will need to sign in again to continue.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button variant="outlined" onClick={handleLogoutDialogClose} sx={{ textTransform: 'none' }}>
              Stay signed in
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmLogout}
              sx={{ textTransform: 'none' }}
            >
              Logout
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default OpenBankingPage;

